/**
 * Download Benchmark Utility
 * 
 * Compares download speeds between Azure Blob Storage and API endpoints.
 * Run from browser console: window.DownloadBenchmark.compare('model-id')
 */

import { configureBlobStorageFromFeatures, getBlobStorageProvider } from '@/types/storage/blobStorage';
import {
  downloadFromAPIParallel,
  downloadFromBlobStorage,
  fetchNodeBlobManifest,
  invalidateModelCache,
  type DownloadProgress,
  type DownloadResult,
} from './modelDownloader';

export interface BenchmarkResult {
  method: 'api' | 'blob';
  modelId: string;
  success: boolean;
  totalBytes: number;
  totalMB: string;
  durationMs: number;
  durationSec: string;
  throughputMBps: string;
  throughputMbps: string; // Megabits per second (network speed)
  chunksDownloaded: number;
  fromCache: boolean;
  error?: string;
  progressHistory: ProgressSnapshot[];
}

interface ProgressSnapshot {
  timestamp: number;
  phase: string;
  percent: number;
  bytesDownloaded: number;
  totalBytes: number;
}

export interface ComparisonResult {
  modelId: string;
  api: BenchmarkResult | null;
  blob: BenchmarkResult | null;
  winner: 'api' | 'blob' | 'tie' | 'incomplete';
  speedDifference: string;
  summary: string;
}

/**
 * Runs a benchmark for a single download method.
 */
async function runBenchmark(
  method: 'api' | 'blob',
  modelId: string,
  blobUrl?: string
): Promise<BenchmarkResult> {
  console.log(`\n[Benchmark] Starting ${method.toUpperCase()} download...`);

  // Clear cache first to ensure fair test
  console.log(`[Benchmark] Clearing cache for model ${modelId}...`);
  await invalidateModelCache(modelId);

  const progressHistory: ProgressSnapshot[] = [];
  const startTime = performance.now();

  const onProgress = (progress: DownloadProgress) => {
    progressHistory.push({
      timestamp: performance.now() - startTime,
      phase: progress.phase,
      percent: progress.percent,
      bytesDownloaded: progress.bytesDownloaded,
      totalBytes: progress.totalBytes,
    });

    // Log progress every 10%
    if (progress.percent % 10 === 0 || progress.phase === 'complete') {
      console.log(`[Benchmark] ${method.toUpperCase()}: ${progress.phase} ${progress.percent}% (${(progress.bytesDownloaded / 1024 / 1024).toFixed(1)} MB)`);
    }
  };

  let result: DownloadResult;

  if (method === 'api') {
    result = await downloadFromAPIParallel(modelId, {
      onProgress,
      forceDownload: true,
    });
  } else {
    if (!blobUrl) {
      return {
        method,
        modelId,
        success: false,
        totalBytes: 0,
        totalMB: '0',
        durationMs: 0,
        durationSec: '0',
        throughputMBps: '0',
        throughputMbps: '0',
        chunksDownloaded: 0,
        fromCache: false,
        error: 'No blob URL available',
        progressHistory: [],
      };
    }

    result = await downloadFromBlobStorage(modelId, blobUrl, {
      onProgress,
      forceDownload: true,
    });
  }

  const endTime = performance.now();
  const durationMs = endTime - startTime;
  const durationSec = durationMs / 1000;
  const totalMB = result.totalBytes / 1024 / 1024;
  const throughputMBps = totalMB / durationSec;
  const throughputMbps = throughputMBps * 8; // Convert MB/s to Mbps

  const benchmarkResult: BenchmarkResult = {
    method,
    modelId,
    success: result.success,
    totalBytes: result.totalBytes,
    totalMB: totalMB.toFixed(2) + ' MB',
    durationMs: Math.round(durationMs),
    durationSec: durationSec.toFixed(2) + ' sec',
    throughputMBps: throughputMBps.toFixed(2) + ' MB/s',
    throughputMbps: throughputMbps.toFixed(2) + ' Mbps',
    chunksDownloaded: result.chunksDownloaded,
    fromCache: result.fromCache,
    error: result.error,
    progressHistory,
  };

  console.log(`[Benchmark] ${method.toUpperCase()} complete:`, {
    duration: benchmarkResult.durationSec,
    throughput: benchmarkResult.throughputMBps,
    networkSpeed: benchmarkResult.throughputMbps,
    size: benchmarkResult.totalMB,
  });

  return benchmarkResult;
}

/**
 * Compares API and Blob Storage download speeds for a model.
 * 
 * Usage from browser console:
 * ```
 * await window.DownloadBenchmark.compare('your-model-id')
 * ```
 */
export async function compareSpeeds(modelId: string): Promise<ComparisonResult> {
  console.log('╔════════════════════════════════════════════════════════════════╗');
  console.log('║           DOWNLOAD SPEED BENCHMARK                             ║');
  console.log('╚════════════════════════════════════════════════════════════════╝');
  console.log(`\nModel ID: ${modelId}`);
  console.log(`Current blob provider: ${getBlobStorageProvider()}`);

  // Get blob URL from manifest
  const manifestResult = await fetchNodeBlobManifest(modelId);
  const manifest = manifestResult.manifest;
  const hasBlobUrl = manifest?.storageType === 'blob' && !!manifest.blobUrl;

  console.log(`\nManifest info:`);
  console.log(`  Storage type: ${manifest?.storageType ?? 'unknown'}`);
  console.log(`  Total nodes: ${manifest?.totalNodes ?? 'unknown'}`);
  console.log(`  Total size: ${manifest?.totalByteSize ? (manifest.totalByteSize / 1024 / 1024).toFixed(2) + ' MB' : 'unknown'}`);
  console.log(`  Blob URL: ${hasBlobUrl ? 'available' : 'not available'}`);

  // Run API benchmark
  console.log('\n' + '─'.repeat(60));
  console.log('TEST 1: API Row Pagination (4 concurrent requests)');
  console.log('─'.repeat(60));
  const apiResult = await runBenchmark('api', modelId);

  // Run Blob benchmark if URL is available
  let blobResult: BenchmarkResult | null = null;
  if (hasBlobUrl) {
    console.log('\n' + '─'.repeat(60));
    console.log('TEST 2: Azure Blob Storage (4 concurrent requests)');
    console.log('─'.repeat(60));
    blobResult = await runBenchmark('blob', modelId, manifest!.blobUrl);
  } else {
    console.log('\n' + '─'.repeat(60));
    console.log('TEST 2: Azure Blob Storage - SKIPPED (no blob URL available)');
    console.log('─'.repeat(60));
    console.log('To test blob storage:');
    console.log('1. Ensure blob storage feature flag is enabled');
    console.log('2. Publish the model to blob storage from the backend');
  }

  // Calculate comparison
  let winner: 'api' | 'blob' | 'tie' | 'incomplete' = 'incomplete';
  let speedDifference = 'N/A';
  let summary = '';

  if (apiResult.success && blobResult?.success) {
    const apiMBps = parseFloat(apiResult.throughputMBps);
    const blobMBps = parseFloat(blobResult.throughputMBps);

    if (Math.abs(apiMBps - blobMBps) < 0.5) {
      winner = 'tie';
      speedDifference = 'Within 0.5 MB/s';
      summary = 'Both methods perform similarly for this model.';
    } else if (apiMBps > blobMBps) {
      winner = 'api';
      const diff = ((apiMBps - blobMBps) / blobMBps * 100).toFixed(1);
      speedDifference = `API is ${diff}% faster`;
      summary = `API row pagination is faster (${apiResult.throughputMBps} vs ${blobResult.throughputMBps}).`;
    } else {
      winner = 'blob';
      const diff = ((blobMBps - apiMBps) / apiMBps * 100).toFixed(1);
      speedDifference = `Blob is ${diff}% faster`;
      summary = `Azure Blob Storage is faster (${blobResult.throughputMBps} vs ${apiResult.throughputMBps}).`;
    }
  } else if (apiResult.success && !blobResult) {
    summary = 'Blob storage test skipped - no blob URL available.';
  } else if (!apiResult.success) {
    summary = `API download failed: ${apiResult.error}`;
  } else if (blobResult && !blobResult.success) {
    summary = `Blob download failed: ${blobResult.error}`;
  }

  const comparison: ComparisonResult = {
    modelId,
    api: apiResult,
    blob: blobResult,
    winner,
    speedDifference,
    summary,
  };

  // Print summary
  console.log('\n' + '═'.repeat(60));
  console.log('BENCHMARK RESULTS');
  console.log('═'.repeat(60));

  console.log('\n┌─────────────┬──────────────┬──────────────┬──────────────┐');
  console.log('│ Method      │ Duration     │ Throughput   │ Network      │');
  console.log('├─────────────┼──────────────┼──────────────┼──────────────┤');
  console.log(`│ API         │ ${apiResult.durationSec.padEnd(12)} │ ${apiResult.throughputMBps.padEnd(12)} │ ${apiResult.throughputMbps.padEnd(12)} │`);
  if (blobResult) {
    console.log(`│ Blob        │ ${blobResult.durationSec.padEnd(12)} │ ${blobResult.throughputMBps.padEnd(12)} │ ${blobResult.throughputMbps.padEnd(12)} │`);
  } else {
    console.log('│ Blob        │ SKIPPED      │ N/A          │ N/A          │');
  }
  console.log('└─────────────┴──────────────┴──────────────┴──────────────┘');

  console.log(`\n🏆 Winner: ${winner.toUpperCase()}`);
  console.log(`   ${summary}`);

  return comparison;
}

/**
 * Run API-only benchmark (useful when blob storage isn't configured).
 */
export async function benchmarkAPI(modelId: string): Promise<BenchmarkResult> {
  console.log('╔════════════════════════════════════════════════════════════════╗');
  console.log('║           API DOWNLOAD BENCHMARK                               ║');
  console.log('╚════════════════════════════════════════════════════════════════╝');

  const result = await runBenchmark('api', modelId);

  console.log('\n' + '═'.repeat(60));
  console.log('RESULT');
  console.log('═'.repeat(60));
  console.log(`  Duration:    ${result.durationSec}`);
  console.log(`  Throughput:  ${result.throughputMBps}`);
  console.log(`  Network:     ${result.throughputMbps}`);
  console.log(`  Size:        ${result.totalMB}`);
  console.log(`  Chunks:      ${result.chunksDownloaded}`);

  return result;
}

/**
 * Run Blob-only benchmark.
 */
export async function benchmarkBlob(modelId: string): Promise<BenchmarkResult> {
  console.log('╔════════════════════════════════════════════════════════════════╗');
  console.log('║           BLOB STORAGE DOWNLOAD BENCHMARK                      ║');
  console.log('╚════════════════════════════════════════════════════════════════╝');

  const manifestResult = await fetchNodeBlobManifest(modelId);
  const manifest = manifestResult.manifest;

  if (!manifest?.blobUrl || manifest.storageType !== 'blob') {
    console.error('❌ No blob URL available for this model.');
    console.log('   Ensure the model is published to blob storage.');
    throw new Error('No blob URL available');
  }

  const result = await runBenchmark('blob', modelId, manifest.blobUrl);

  console.log('\n' + '═'.repeat(60));
  console.log('RESULT');
  console.log('═'.repeat(60));
  console.log(`  Duration:    ${result.durationSec}`);
  console.log(`  Throughput:  ${result.throughputMBps}`);
  console.log(`  Network:     ${result.throughputMbps}`);
  console.log(`  Size:        ${result.totalMB}`);
  console.log(`  Chunks:      ${result.chunksDownloaded}`);

  return result;
}

/**
 * Run multiple iterations and calculate average.
 */
export async function benchmarkMultiple(
  modelId: string,
  iterations: number = 3
): Promise<{
  api: { avg: string; min: string; max: string; results: BenchmarkResult[]; };
  blob: { avg: string; min: string; max: string; results: BenchmarkResult[]; } | null;
}> {
  console.log(`Running ${iterations} iterations for each method...`);

  const apiResults: BenchmarkResult[] = [];
  const blobResults: BenchmarkResult[] = [];

  const manifestResult = await fetchNodeBlobManifest(modelId);
  const manifest = manifestResult.manifest;
  const hasBlobUrl = manifest?.storageType === 'blob' && !!manifest.blobUrl;

  for (let i = 0; i < iterations; i++) {
    console.log(`\n━━━ ITERATION ${i + 1}/${iterations} ━━━`);

    // API test
    const apiResult = await runBenchmark('api', modelId);
    apiResults.push(apiResult);

    // Blob test
    if (hasBlobUrl) {
      const blobResult = await runBenchmark('blob', modelId, manifest!.blobUrl);
      blobResults.push(blobResult);
    }

    // Small delay between iterations
    if (i < iterations - 1) {
      console.log('Waiting 2 seconds before next iteration...');
      await new Promise(resolve => setTimeout(resolve, 2000));
    }
  }

  // Calculate statistics
  const calcStats = (results: BenchmarkResult[]) => {
    const throughputs = results.map(r => parseFloat(r.throughputMBps));
    const avg = throughputs.reduce((a, b) => a + b, 0) / throughputs.length;
    const min = Math.min(...throughputs);
    const max = Math.max(...throughputs);
    return {
      avg: avg.toFixed(2) + ' MB/s',
      min: min.toFixed(2) + ' MB/s',
      max: max.toFixed(2) + ' MB/s',
      results,
    };
  };

  const apiStats = calcStats(apiResults);
  const blobStats = blobResults.length > 0 ? calcStats(blobResults) : null;

  // Print summary
  console.log('\n' + '═'.repeat(60));
  console.log(`AGGREGATE RESULTS (${iterations} iterations)`);
  console.log('═'.repeat(60));

  console.log('\n┌─────────────┬──────────────┬──────────────┬──────────────┐');
  console.log('│ Method      │ Average      │ Min          │ Max          │');
  console.log('├─────────────┼──────────────┼──────────────┼──────────────┤');
  console.log(`│ API         │ ${apiStats.avg.padEnd(12)} │ ${apiStats.min.padEnd(12)} │ ${apiStats.max.padEnd(12)} │`);
  if (blobStats) {
    console.log(`│ Blob        │ ${blobStats.avg.padEnd(12)} │ ${blobStats.min.padEnd(12)} │ ${blobStats.max.padEnd(12)} │`);
  }
  console.log('└─────────────┴──────────────┴──────────────┴──────────────┘');

  return { api: apiStats, blob: blobStats };
}

/**
 * Debug interface for download benchmarks.
 * Available at window.DownloadBenchmark in the browser console.
 */
export interface DownloadBenchmarkDebug {
  /** Compare API vs Blob Storage speeds for a model */
  compare: (modelId: string) => Promise<ComparisonResult>;
  /** Benchmark API download only */
  api: (modelId: string) => Promise<BenchmarkResult>;
  /** Benchmark Blob Storage download only */
  blob: (modelId: string) => Promise<BenchmarkResult>;
  /** Run multiple iterations and get averages */
  multiple: (modelId: string, iterations?: number) => Promise<{
    api: { avg: string; min: string; max: string; results: BenchmarkResult[]; };
    blob: { avg: string; min: string; max: string; results: BenchmarkResult[]; } | null;
  }>;
  /** Get current blob storage provider */
  provider: () => string;
  /** Temporarily enable blob storage for testing */
  enableBlob: (provider?: 'azure' | 's3') => void;
  /** Help text */
  help: () => void;
}

const downloadBenchmarkDebug: DownloadBenchmarkDebug = {
  compare: compareSpeeds,
  api: benchmarkAPI,
  blob: benchmarkBlob,
  multiple: benchmarkMultiple,
  provider: getBlobStorageProvider,

  enableBlob(provider: 'azure' | 's3' = 'azure') {
    const feature = provider === 'azure' ? 'BlobStorageAzure' : 'BlobStorageS3';
    console.log(`Temporarily enabling blob storage: ${provider}`);
    configureBlobStorageFromFeatures([feature]);
    console.log(`Provider is now: ${getBlobStorageProvider()}`);
  },

  help() {
    console.log(`
╔════════════════════════════════════════════════════════════════╗
║           DOWNLOAD BENCHMARK - HELP                            ║
╚════════════════════════════════════════════════════════════════╝

COMMANDS:

  DownloadBenchmark.compare('model-id')
    Run full comparison between API and Blob Storage.
    Returns detailed timing, throughput, and comparison results.

  DownloadBenchmark.api('model-id')
    Benchmark API download only (row pagination, 4 concurrent).

  DownloadBenchmark.blob('model-id')
    Benchmark Blob Storage download only (byte ranges, 4 concurrent).
    Requires model to be published to blob storage.

  DownloadBenchmark.multiple('model-id', 3)
    Run multiple iterations and calculate average throughput.
    Helps account for network variance.

  DownloadBenchmark.provider()
    Check currently configured blob storage provider.

  DownloadBenchmark.enableBlob('azure')
    Temporarily enable blob storage for testing.
    Options: 'azure', 's3'

EXAMPLE:

  // Run full comparison
  const result = await DownloadBenchmark.compare('12345678-abcd-...');
  
  // Check if blob was faster
  console.log(result.winner);  // 'api', 'blob', or 'tie'
  console.log(result.summary);

  // Get averages over 3 runs
  const stats = await DownloadBenchmark.multiple('model-id', 3);
  console.log('API avg:', stats.api.avg);
  console.log('Blob avg:', stats.blob?.avg);

NOTES:

  - Cache is cleared before each test for fair comparison
  - Throughput shown in MB/s (megabytes) and Mbps (megabits)
  - Network conditions affect results - run multiple iterations
  - Blob storage requires CORS to be configured on Azure/S3
`);
  },
};

// Expose on window for console access
if (typeof window !== 'undefined') {
  (window as any).DownloadBenchmark = downloadBenchmarkDebug;
}

// Type declaration for window global
declare global {
  interface Window {
    DownloadBenchmark: DownloadBenchmarkDebug;
  }
}

